import { useEffect, useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import type { Notification, NotificationReadFilter } from '../../models/Notification.types';
import { NotificationItem } from './NotificationItem';

interface NotificationListProps {
  notifications: Notification[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  readFilter: NotificationReadFilter;
  onFetchNextPage: () => void;
  onNotificationOpen: (notification: Notification) => void;
  onMarkAsReadOnly: (id: number) => void;
  onArchive: (id: number) => void;
}

export const NotificationList = ({
  notifications,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  readFilter,
  onFetchNextPage,
  onNotificationOpen,
  onMarkAsReadOnly,
  onArchive,
}: NotificationListProps) => {
  const { t } = useTranslation('notificationsModule');
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [timeTick, setTimeTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setTimeTick((k) => k + 1);
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        onFetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, onFetchNextPage],
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-icons" />
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-subtext text-sm">
        {t(`notifications.empty.${readFilter}`)}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-1 max-h-[400px] overflow-y-auto"
      data-time-tick={timeTick}
    >
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          readFilter={readFilter}
          onOpen={onNotificationOpen}
          onMarkAsReadOnly={onMarkAsReadOnly}
          onArchive={onArchive}
        />
      ))}

      <div ref={loadMoreRef} className="h-1" />

      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="w-4 h-4 animate-spin text-icons" />
        </div>
      )}
    </div>
  );
};
